import React, { useState } from "react";
import { NewStyledModal } from "../../Shared/NewStyledModal";
import Selector from "../../Shared/Selector";
import { Box, Button, TextField } from "@material-ui/core";
import useStyles from "./styles";
import { useFunctionInterval } from "../../../utils";
import { toast } from "react-toastify";
import { useEffect } from "react";
import { createCpfMask, createCnpjMask } from "../../../utils/inputMasks";

const initialFormValues = {
  client_name: '',
  client_document: '',
  client_nickname: '',
  contract_number: '',
  transporter_name: '',
  transporter_nickname:'',
  transporter_document: ''
};

const initialOptionsValues = {
  clients: [],
  transporters: []
}

export function ModalAddQuota({
  isModalOpen,
  handleModalClose,
  handleSave,
  loadClientsByNickname,
  loadClientsByDocument,
  loadTransportersByNickname,
  loadTransportersByDocument,
}) {
  const classes = useStyles();

  const [feedback, setFeedback] = useState({});
  const [options, setOptions] = useState(initialOptionsValues);
  const [formValues, setFormValues] = useState(initialFormValues);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedTransporter, setSelectedTransporter] = useState(null);


  useEffect(() => {
    if (feedback.success) {
      toast.success(feedback.message);
    } else if (feedback.message) {
      toast.error(feedback.message);
    }
  }, [feedback]);

  const searchOptionsApiMethods = {
    clients: {
      nickname: async (nickname) => await loadClientsByNickname(nickname),
      document: async (document) => await loadClientsByDocument(document),
    },
    transporters: {
      nickname: async (nickname) => await loadTransportersByNickname(nickname),
      document: async (document) => await loadTransportersByDocument(document),
    }
  }

  async function handleSearchOnApi(searchParam, { model, endpoint}) {
    if ((searchParam === null && !model && !endpoint) || (searchParam !== null && searchParam.length < 3)) {
      return;
    }
    return updateOptions(await searchOptionsApiMethods[model][endpoint](searchParam), {source: model, label: endpoint});
  }

  function mapApiReturnToSelectOptions(apiReturn, label) {
    if (!apiReturn || (Array.isArray(apiReturn) && apiReturn.length === 0)) {
      return [];
    }
    return apiReturn.map(item => ({
      label: item[label],
      name: item.name,
      nickname: item.nickname,
      document: item.document,
      value: item.document,
    }));
  }

  function updateOptions(apiReturn, data) {
    return setOptions({
      ...options,
      [data.source]: mapApiReturnToSelectOptions(apiReturn, data.label)
    });
  }

  const searchForOptionsOnApi = useFunctionInterval((nickname, apiInfo) => {
    handleSearchOnApi(nickname, {...apiInfo})
  }, 500);

  function handleFormSubmit(values) {
    if (handleSave(values)) {
      closeModalAndClearData();
      handleModalClose();
      return setFeedback({success: true, message: "Cota adicionada com sucesso."});
    }
    return setFeedback({success: false, message: "Erro ao adicionar cota: Cliente e transportadora já adicionada."});
  }

  function closeModalAndClearData() {
    handleModalClose();
    clearForm();
  }

  function clearClientForm() {
    setSelectedClient(null);
    setFormValues({...formValues, client_name: '', client_document: '', client_nickname: ''});
  }

  function clearTransporterForm() {
    setSelectedTransporter(null);
    setFormValues({...formValues, transporter_name: '', transporter_document: '', transporter_nickname: ''});
  }

  function clearForm() {
    setFormValues(initialFormValues);
    setSelectedClient(null);
    setSelectedTransporter(null);
    setOptions(initialOptionsValues);
  }

  function getNickNameLabel(source) {
    return source.nickname;
  }

  function getDocumentLabel(source) {
    const { document } = source;
    return document.length < 11 ? createCpfMask(document) : createCnpjMask(document);
  }

  const updateFormValues = (values) => {
    return setFormValues({
      ...formValues,
      ...values
    });
  }

  function shouldSubmitButtonBeDisabled() {
    return !(
        formValues.client_name &&
        formValues.client_document
      );
  }

  function modalFooter() {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end'
        }}
      >
        <Button
          variant="contained"
          color="primary"
          onClick={() => handleFormSubmit(formValues)}
          size="large"
          className={classes.modalFooterButton}
          disabled={shouldSubmitButtonBeDisabled()}
        >
          Salvar
        </Button>
      </Box>
    );
  };

  return (
    <NewStyledModal
      isModalOpen={isModalOpen}
      handleModalClose={closeModalAndClearData}
      modalTitle="Adicionar cotas para Cliente / Fornecedor"
      footerContent={modalFooter}
    >

      <div id="client-quota-add-modal-client-div">
        <h4 className={classes.fontPrimaryColor}>Cliente / Fornecedor *</h4>
        <Box sx={{
          display: 'grid',
          gap: 16,
          gridTemplateColumns: 'repeat(2, 1fr)'
        }}>
          <Selector
            value={selectedClient || ''}
            options={options.clients}
            placeholder="Digite Apelido Cliente / Fornecedor"
            data-cy="client-quota-add-modal-client-nickname"
            id="client-quota-add-modal-client-nickname"
            noOptionsMessage="Consulta não retornou registro, por favor, acessar o cadastro de Cliente/Fornecedor e realizar o cadastro!"
            search
            isClearable
            getOptionLabel={getNickNameLabel}
            onInputChange={(input) => {
              if (input.length > 60) {
                return input.slice(0, 60);
              }
              searchForOptionsOnApi(input, {model: 'clients', endpoint: 'nickname'});
            }}
            onChange={(selected) => {
              if (selected === null) {
                return clearClientForm();
              }
              setSelectedClient(selected);
              updateFormValues({
                client_name: selected.name,
                client_nickname: selected.nickname,
                client_document: selected.value
              });
            }}
            styleVersion="v2"
          ></Selector>
          <Selector
            value={selectedClient || ''}
            options={options.clients}
            placeholder="Digite CPF/CNPJ Cliente / Fornecedor"
            data-cy="client-quota-add-modal-client-cpf-cnpj"
            id="client-quota-add-modal-client-cpf-cnpj"
            noOptionsMessage="Consulta não retornou registro, por favor, acessar o cadastro de Cliente/Fornecedor e realizar o cadastro!"
            search
            isClearable
            getOptionLabel={getDocumentLabel}
            onInputChange={(input) => {
              if (input.length > 60) {
                return input.slice(0, 60);
              }
              searchForOptionsOnApi(input, { model: 'clients', endpoint: 'document' });
            }}
            onChange={(selected) => {
              if (selected === null) {
                return clearClientForm();
              }
              setSelectedClient(selected);
              updateFormValues({
                client_name: selected.name,
                client_nickname: selected.nickname,
                client_document: selected.value
              });
            }}
            styleVersion="v2"
          ></Selector>
        </Box>
        <Box className={classes.marginTop} sx={{display: 'grid', gridTemplateColumns: "repeat(1, 1fr)"}}>
          <TextField
            label="Razão social Cliente / Fornecedor"
            data-cy="client-quota-add-modal-client-input-name"
            id="client-quota-add-modal-client-input-client-name"
            size="small"
            type="text"
            variant="outlined"
            disabled
            value={formValues.client_name}
            InputLabelProps={{ style: { fontSize: 14 } }}
          />
        </Box>
      </div>
      <div id="client-quota-add-modal-transporter-div">
      <h4 className={classes.fontPrimaryColor}>Transportadora</h4>
        <Box sx={{
          display: 'grid',
          gap: 16,
          gridTemplateColumns: 'repeat(2, 1fr)'
        }}>
          <Selector
            value={selectedTransporter || ''}
            options={options.transporters}
            placeholder="Digite Apelido Transportadora"
            data-cy="client-quota-add-modal-client-transporter-nickname"
            id="client-quota-add-modal-client-transporter-nickname"
            noOptionsMessage="Consulta não retornou registro, por favor, acessar o cadastro da Transportadora e realizar o cadastro!"
            search
            isClearable
            getOptionLabel={getNickNameLabel}
            onInputChange={(input) => {
              if (input.length > 60) {
                return input.slice(0, 60);
              }
              searchForOptionsOnApi(input, { model: 'transporters', endpoint: 'nickname' });
            }}
            onChange={(selected) => {
              if (selected === null) {
                return clearTransporterForm();
              }
              setSelectedTransporter(selected);
              updateFormValues({
                transporter_name: selected.name,
                transporter_nickname: selected.nickname,
                transporter_document: selected.value
              });
            }}
            styleVersion="v2"
          ></Selector>
          <Selector
            value={selectedTransporter || ''}
            options={options.transporters}
            placeholder="Digite CPF/CNPJ Transportadora"
            data-cy="client-quota-add-modal-transporter-cpf-cnpj"
            id="client-quota-add-modal-transporter-cpf-cnpj"
            noOptionsMessage="Consulta não retornou registro, por favor, acessar o cadastro da Transportadora e realizar o cadastro!"
            search
            isClearable
            getOptionLabel={getDocumentLabel}
            onInputChange={(input) => {
              if (input.length > 60) {
                return input.slice(0, 60);
              }
              searchForOptionsOnApi(input, { model: 'transporters', endpoint: 'document' });
            }}
            onChange={(selected) => {
              if (selected === null) {
                return clearTransporterForm();
              }
              setSelectedTransporter(selected);
              updateFormValues({
                transporter_name: selected.name,
                transporter_nickname: selected.nickname,
                transporter_document: selected.value
              });
            }}
            styleVersion="v2"
          ></Selector>
        </Box>
        <Box className={classes.marginTop} sx={{display: 'grid', gridTemplateColumns: "repeat(1, 1fr)"}}>
          <TextField
            label="Razão social Transportadora"
            data-cy="client-quota-add-modal-client-input-name"
            id="client-quota-add-modal-client-input-transporter-name"
            size="small"
            type="text"
            variant="outlined"
            disabled
            value={formValues.transporter_name}
            InputLabelProps={{ style: { fontSize: 14 } }}
          />
        </Box>
      </div>
      <div id="client-quota-add-modal-documents-div">
        <h4 className={classes.fontPrimaryColor}>Documentos</h4>
        <Box sx={{
          display: 'grid',
          gap: 16,
          gridTemplateColumns: 'repeat(2, 1fr)'
        }}
        >
          <TextField
            label="Digite o Número do Contrato, Load ou Pedido"
            data-cy="client-quota-add-modal-client-input-name"
            id="client-quota-add-modal-client-input-transporter-name"
            size="small"
            type="text"
            variant="outlined"
            style={{ backgroundColor: '#FFFFFF' }}
            InputLabelProps={{ style: { fontSize: 14 } }}
            inputProps={{ maxLength: 20 }}
            onChange={(e) => {
              if (!e.target.value || e.target.value === '') {
                return;
              }

              updateFormValues({
                contract_number: e.target.value,
              });
            }}
          />
        </Box>
      </div>
    </NewStyledModal>
  );
}
